import Store from 'store/store';
import * as jsonPointer from 'utils/jsonPointer';
import { isPlaceholder } from 'utils/data';
import { getCurrentMovieUrl, isBaseRegion, isFocusStudio } from 'utils/studioConfig';
import slugify from 'utils/slugify';

export function missingTitle(data) {
	const title = typeof data === 'string' ? data : (data?.content?.title || data?.base_movie?.content?.title);
	if (!data || !title || title === '') {
		return 'A movie title is required';
	}
	return null;
}

export function duplicateTitle(data) {
	const title = typeof data === 'string' ? data : (data?.content?.title || data?.base_movie?.content?.title);
	if (!title) return null;
	const movies = Store.get().data?.movies || {};
	const existing = Object.values(movies).some(movie => movie.id !== data.id && movie.content?.title?.toLowerCase() === title.toLowerCase());
	return existing && 'A movie with this title already exists, please use a different title.';
}

export function missingSlug(data) {
	const slug = typeof data === 'string' ? data : data?.content?.slug;
	if (!slug) {
		return 'A movie URL is required';
	}
	return null;
}

export function duplicateSlug(data) {
	const slug = typeof data === 'string' ? data : data?.content?.slug;
	if (!slug) return null;
	const movies = Store.get().data?.movies || {};
	const existing = Object.values(movies).some(movie => movie.id !== data.id && movie.content?.slug === slug);
	return existing && 'A movie with this URL already exists, please use a different URL.';
}

export function validateSlug(data) {
	const slug = typeof data === 'string' ? data : data?.content?.slug;
	if (!slug) return null;
	const slugifiedSlug = slugify(slug, { removeDiacritics: true });
	return slug !== slugifiedSlug && 'The URL can only contain letters, numbers, and hyphens.';
}

function isValidUrl(str) {
	try {
		const url = new URL(str);
		return url.protocol === 'http:' || url.protocol === 'https:';
	} catch {
		return false;
	}
}

export function urlValidationError(data) {	
	const url = typeof data === 'string' ? data : data?.content?.url;
	if (url === null || url === undefined) return null;

	if (url === getCurrentMovieUrl(data.content?.slug)) return 'The URL cannot be the same as the default movie URL. Please use a different URL.';
	return !isValidUrl(url) && 'Please use a URL in the format of https://example.com/';
}

export function missingReleaseDate(data) {
	const releaseDate = typeof data === 'string' ? data : data?.content?.dates?.release;
	if (!releaseDate) {
		return 'A release date is required';
	}
	return null;
}

export function missingHideDate(data) {
	if (!data || (data.content?.dates?.hideFromCarouselsAfter === '')) {
		return 'A hide date is required if set.';
	}
	return null;
}

export function missingTicketingDate(data) {
	if (isFocusStudio()) return null; // Ticketing date is not required for Focus Features movies
	if (!data || isPlaceholder(data)) return null;
	const ticketingDate = data?.content?.dates?.ticketing;
	if (!ticketingDate || ticketingDate === '') {
		return 'A ticketing date is required';
	}
	return null;
}

export function missingHomeEntDate(data) {
	if (isFocusStudio()) return null; // Home Entertainment date is not required for Focus Features movies
	if (!data || isPlaceholder(data)) return null;
	const homeEntActive = data?.content?.pages?.homeEnt?.active;
	if (!homeEntActive) return null; // Home Entertainment date is not required if the home entertainment page is not active
	const homeEntDate = data?.content?.dates?.homeEnt;
	if (!homeEntDate || homeEntDate === '') {
		return 'A home entertainment date is required';
	}
	return null;
}

export function IMDBIdValidationError(data) {
	if (!data?.content?.dataLayer?.imdbId) return null;
	const imdbId = data.content?.dataLayer?.imdbId;
	const imdbIdPattern = /^tt\d{7,8}$/;
	return !imdbIdPattern.test(imdbId) && 'Must be in the format of "tt1234567".';
}

export function missingOnesheet(data) {
	if (!data || (!isPlaceholder(data) && !data.content?.onesheet?.url)) {
		return 'Movie is missing a onesheet.';
	}
	return null;
}

export function missingHeroImage(data) {
	const heroStill = isFocusStudio()
		? data?.content?.hero?.still?.url
		: (data?.content?.hero?.desktop?.still?.url || data?.base_movie?.content?.hero?.desktop?.still?.url);
	if (!data || (!isPlaceholder(data) && !heroStill)) {
		return 'Movie is missing a hero image.';
	}
	return null;
}

export function missingShowtimesSource(data) {	
	const showtimesActive = data?.content?.pages?.showtimes?.active;
	const showtimesId = data?.content?.showtimesId || data?.base_movie?.content?.showtimesId;
	if (showtimesActive && !showtimesId) {
		return 'A showtimes source is required when the showtimes page is live.';
	}

	return null;
}


export function hasErrors(path) {
	// if (!path || !path?.startsWith('/movies/')) return false;
	// const data = jsonPointer.get(Store.get().data, path);
	// return (
	// 	missingTitle(data)
	// 	|| duplicateTitle(data)
	// 	// || missingReleaseDate(data)
	// );
	return listErrors(path).errors.length;
}

export function hasWarnings(path) {
	// if (!path || !path?.startsWith('/movies/')) return false;
	// const data = jsonPointer.get(Store.get().data, path);
	// return (
	// 	missingOnesheet(data)
	// 	|| missingHeroImage(data)
	// );
	return listErrors(path).warnings.length;
}

export function listErrors(path) {
	// for now these checks only apply to movies,
	// but could add checks for sections later
	if (!path || !path?.startsWith('/movies/')) {
		return { errors: [], warnings: [] };
	}

	const data = jsonPointer.get(Store.get().data, path);

	if (isBaseRegion()) {
		const errors = [
			missingTitle(data),
			duplicateTitle(data),
			IMDBIdValidationError(data),
		].filter(Boolean);
		const warnings = [];

		return { errors, warnings };
	}

	const errors = [
		missingTitle(data),
		duplicateTitle(data),
		missingSlug(data),
		duplicateSlug(data),
		validateSlug(data),
		missingReleaseDate(data),
		missingHomeEntDate(data),
		missingHideDate(data),
		urlValidationError(data),
		IMDBIdValidationError(data),
		missingShowtimesSource(data),
	].filter(Boolean);

	const warnings = [];

	const missingOnesheetError = missingOnesheet(data);
	const missingHeroImageError = missingHeroImage(data);
	if (missingOnesheetError || missingHeroImageError) {
		// const missing = [missingOnesheetError && 'a onesheet', missingHeroImageError && 'a hero image'].filter(Boolean).join(' and ');
		const missing = new Intl.ListFormat().format([
			missingOnesheetError && 'a onesheet',
			missingHeroImageError && 'a hero image',
		].filter(Boolean));
		warnings.push(`This movie is missing ${missing}. This will prevent it from showing in hero carousels.`);
	}

	return { errors, warnings };
}